import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, DollarSign, Heart, Brain, Users, GraduationCap, Briefcase } from 'lucide-react';
import { useGameStore } from '../store/gameStore';
import { GameEvent, EventChoice } from '../types/game';

interface EventModalProps {
  event: GameEvent | null;
  isOpen: boolean;
  onClose: () => void;
}

const EventModal: React.FC<EventModalProps> = ({ event, isOpen, onClose }) => {
  const { money, processEventChoice } = useGameStore();

  if (!event || !isOpen) return null;

  const handleChoiceSelect = (choice: EventChoice) => {
    processEventChoice(event.id, choice.id);
    onClose();
  };

  const canAfford = (choice: EventChoice) => {
    // Always allow - debt is permitted
    return true;
  };

  const getEventIcon = (type: string) => {
    const icons = {
      money: DollarSign,
      health: Heart,
      social: Users,
      career: Briefcase,
      random: Brain,
    };
    return icons[type as keyof typeof icons] || Brain;
  };

  const getEventColor = (type: string) => {
    const colors = {
      money: 'from-emerald-500 to-green-600',
      health: 'from-red-500 to-pink-600',
      social: 'from-blue-500 to-purple-600',
      career: 'from-amber-500 to-orange-600',
      random: 'from-purple-500 to-indigo-600',
    };
    return colors[type as keyof typeof colors] || 'from-gray-500 to-gray-600';
  };

  const getStatChangeIcon = (stat: string) => {
    const icons = {
      health: Heart,
      happiness: Brain,
      wealth: DollarSign,
      relationships: Users,
      education: GraduationCap,
      career: Briefcase,
    };
    return icons[stat as keyof typeof icons] || Brain;
  };

  const getStatChangeColor = (change: number) => {
    if (change > 0) return 'text-emerald-400';
    if (change < 0) return 'text-red-400';
    return 'text-slate-400';
  };

  const Icon = getEventIcon(event.type);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className={`bg-gradient-to-r ${getEventColor(event.type)} p-6 text-white`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-3xl backdrop-blur-sm">
                    {event.icon || <Icon size={32} />}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold mb-2">{event.title}</h2>
                    <p className="text-white/90 text-lg">{event.description}</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-4">Choose your response:</h3>
                <div className="space-y-4">
                  {event.choices?.map((choice, index) => (
                    <motion.button
                      key={choice.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handleChoiceSelect(choice)}
                      disabled={!canAfford(choice)}
                      className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                        canAfford(choice)
                          ? 'border-slate-600 hover:border-blue-500 hover:bg-slate-700/50 bg-slate-700/30'
                          : 'border-red-600 bg-red-900/20 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-semibold text-white">{choice.title}</h4>
                        {choice.cost && (
                          <div className={`text-lg font-bold ${canAfford(choice) ? 'text-emerald-400' : 'text-red-400'}`}>
                            ${choice.cost.toLocaleString()}
                          </div>
                        )}
                      </div>
                      <p className="text-slate-300 mb-3">{choice.description}</p>
                      
                      {/* Stat Changes Preview */}
                      {choice.effects && Object.keys(choice.effects).length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(choice.effects).map(([stat, change]) => {
                            const StatIcon = getStatChangeIcon(stat);
                            return (
                              <div
                                key={stat}
                                className={`flex items-center space-x-1 px-2 py-1 rounded-lg bg-slate-600/50 ${getStatChangeColor(change as number)}`}
                              >
                                <StatIcon size={14} />
                                <span className="text-sm font-medium capitalize">{stat}</span>
                                <span className="text-sm font-bold">
                                  {change > 0 ? '+' : ''}{change}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {!canAfford(choice) && choice.cost && (
                        <div className="text-red-400 text-sm mt-2 flex items-center space-x-1">
                          <DollarSign size={14} />
                          <span>Insufficient funds</span>
                        </div>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Current Money Display */}
              <div className="bg-slate-700/50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="text-emerald-400" size={20} />
                    <span className="text-emerald-400 font-medium">Your Money</span>
                  </div>
                  <span className="text-2xl font-bold text-emerald-400">
                    ${money.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EventModal;
